import React, { useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BookOpenIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import DiagramEditor from '../diagram/DiagramEditor';
import { parseDiagram } from '../diagram/types';
import DrillPicker from '../drills/DrillPicker';
import DrillFormModal from '../drills/DrillFormModal';
import type { SaveDrillRequest } from '../../types/drill';
import type { TrainingPlanItem } from '../../types/trainingSession';
import { useToast } from '../../contexts/ToastContext';
import { DiagramPreview } from './PlanView';
import { MAX_PLAN_ITEMS, appendItems, drillToBlock, emptyItem, isBlankItem } from './planUtils';
import AiButton from '../ai/AiButton';
import AiTrainingPlanModal, { AiPlanDefaults } from '../ai/AiTrainingPlanModal';
import HelpHint from '../help/HelpHint';
import { useAiStatus, refreshAiStatus } from '../../hooks/useAiStatus';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';

interface PlanBlocksEditorProps {
  items: TrainingPlanItem[];
  setItems: React.Dispatch<React.SetStateAction<TrainingPlanItem[]>>;
  /** További gombok az "Új blokk" / "Gyakorlattárból" mellé (pl. sablon betöltése). */
  extraActions?: React.ReactNode;
  /** Az AI edzéstervnél előre kitöltött korosztály és időtartam (az edzés adataiból). */
  aiDefaults?: AiPlanDefaults;
  /** Az AI által javasolt cél - ha a szülő kezeli a célt, itt kapja meg. */
  onAiGoal?: (goal: string) => void;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Az edzésterv blokkjainak szerkesztése: név, perc, leírás, rajz, sorrend,
 * beemelés a gyakorlattárból és mentés a gyakorlattárba. Az edzés tervénél és
 * az edzéssablonoknál is ez szerkeszti a blokkokat.
 */
const PlanBlocksEditor: React.FC<PlanBlocksEditorProps> = ({ items, setItems, extraActions, aiDefaults, onAiGoal }) => {
  const { showToast } = useToast();
  const ai = useAiStatus();
  const [aiPlanOpen, setAiPlanOpen] = useState(false);
  // Melyik blokk rajzát készíti épp az AI
  const [aiDiagramIndex, setAiDiagramIndex] = useState<number | null>(null);
  // Melyik blokk rajzát szerkesztjük (null = a szerkesztő zárva)
  const [diagramIndex, setDiagramIndex] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Blokk mentése a gyakorlattárba: az előtöltött űrlap adatai
  const [drillDraft, setDrillDraft] = useState<SaveDrillRequest | null>(null);

  const updateItem = (index: number, change: Partial<TrainingPlanItem>) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...change } : item)));
  };

  const moveItem = (index: number, delta: number) => {
    setItems((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const full = items.length >= MAX_PLAN_ITEMS;

  const drawWithAi = async (index: number) => {
    const item = items[index];
    setAiDiagramIndex(index);
    try {
      const diagram = await aiAPI.diagram(item.description ?? '', item.title || undefined);
      updateItem(index, { diagram });
      showToast('Kész a rajz - kattints rá, ha módosítanád.');
    } catch (err) {
      showToast(getErrorMessage(err, 'Az AI most nem tudott rajzot készíteni.'), 'error');
    } finally {
      setAiDiagramIndex(null);
      refreshAiStatus();
    }
  };

  return (
    <>
      {aiPlanOpen && (
        <AiTrainingPlanModal
          defaults={aiDefaults}
          hasContent={items.some((i) => !isBlankItem(i))}
          onClose={() => setAiPlanOpen(false)}
          onGenerated={(result, mode) => {
            setItems((prev) => (mode === 'append' ? appendItems(prev, result.items) : result.items.map((i) => ({ ...i }))));
            if (result.goal && onAiGoal) onAiGoal(result.goal);
            showToast(
              `Kész az edzésterv: ${result.items.length} blokk${result.fromLibraryCount > 0 ? `, ebből ${result.fromLibraryCount} a gyakorlattárból` : ''}. Nézd át, és mentsd el.`
            );
          }}
        />
      )}
      {pickerOpen && (
        <DrillPicker
          onClose={() => setPickerOpen(false)}
          onPick={(drill) => {
            setItems((prev) => appendItems(prev, [drillToBlock(drill)]));
            showToast(`„${drill.title}” hozzáadva az edzéstervhez.`);
          }}
        />
      )}
      {drillDraft && (
        <DrillFormModal
          drill={null}
          initial={drillDraft}
          onClose={() => setDrillDraft(null)}
          onSaved={(drill) => {
            setDrillDraft(null);
            showToast(`„${drill.title}” elmentve a gyakorlattárba.`);
          }}
        />
      )}
      {diagramIndex !== null && items[diagramIndex] && (
        <DiagramEditor
          initial={parseDiagram(items[diagramIndex].diagram)}
          title={items[diagramIndex].title || `${diagramIndex + 1}. blokk`}
          onCancel={() => setDiagramIndex(null)}
          onSave={(diagram) => {
            updateItem(diagramIndex, { diagram: diagram ? JSON.stringify(diagram) : null });
            setDiagramIndex(null);
          }}
        />
      )}

      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-md border border-gray-200 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-sm font-medium text-gray-500">{index + 1}.</span>
            <input
              type="text"
              maxLength={200}
              value={item.title}
              onChange={(e) => updateItem(index, { title: e.target.value })}
              placeholder="Blokk neve (pl. Bemelegítés)"
              className={inputClass}
              aria-label={`${index + 1}. blokk neve`}
            />
            <input
              type="number"
              min={0}
              max={600}
              value={item.durationMinutes ?? ''}
              onChange={(e) => updateItem(index, { durationMinutes: e.target.value ? Number(e.target.value) : null })}
              placeholder="perc"
              className="w-20 shrink-0 px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={`${index + 1}. blokk időtartama percben`}
            />
          </div>
          <textarea
            rows={2}
            maxLength={2000}
            value={item.description ?? ''}
            onChange={(e) => updateItem(index, { description: e.target.value })}
            placeholder="Leírás, szervezés, eszközök (opcionális)"
            className={inputClass}
            aria-label={`${index + 1}. blokk leírása`}
          />
          {item.diagram && (
            <button
              type="button"
              onClick={() => setDiagramIndex(index)}
              className="block w-full max-w-sm overflow-hidden rounded-md ring-1 ring-gray-200 hover:ring-primary-400"
              aria-label={`${index + 1}. blokk rajzának szerkesztése`}
            >
              <DiagramPreview json={item.diagram} title={item.title} />
            </button>
          )}
          <div className="flex flex-wrap items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setDiagramIndex(index)}
              className="mr-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50"
            >
              <PencilSquareIcon className="h-4 w-4" />
              {item.diagram ? 'Rajz szerkesztése' : 'Rajz hozzáadása'}
            </button>
            {ai.available && !item.diagram && (item.description?.trim().length ?? 0) >= 20 && (
              <AiButton
                variant="link"
                onClick={() => drawWithAi(index)}
                loading={aiDiagramIndex === index}
                disabled={aiDiagramIndex !== null}
                loadingText="Rajzolok…"
                title="Az AI a leírás alapján megrajzolja a gyakorlatot"
                className="px-2 py-1"
              >
                Rajz AI-val
              </AiButton>
            )}
            {item.diagram && (
              <button
                type="button"
                onClick={() => updateItem(index, { diagram: null })}
                className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-red-600"
              >
                Rajz törlése
              </button>
            )}
            {item.title.trim() && (
              <button
                type="button"
                onClick={() =>
                  setDrillDraft({
                    title: item.title,
                    durationMinutes: item.durationMinutes ?? null,
                    description: item.description ?? '',
                    diagram: item.diagram ?? null,
                  })
                }
                title="A blokk mentése a gyakorlattárba, hogy más edzésekhez is fel lehessen használni"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              >
                Mentés a tárba
              </button>
            )}
            <button
              type="button"
              onClick={() => moveItem(index, -1)}
              disabled={index === 0}
              aria-label="Feljebb"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-30"
            >
              <ArrowUpIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => moveItem(index, 1)}
              disabled={index === items.length - 1}
              aria-label="Lejjebb"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-30"
            >
              <ArrowDownIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => removeItem(index)}
              aria-label="Blokk törlése"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          onClick={() => setItems((prev) => [...prev, emptyItem()])}
          disabled={full}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4" />
          Új blokk
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={full}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline disabled:opacity-50"
        >
          <BookOpenIcon className="h-4 w-4" />
          Gyakorlattárból
        </button>
        {extraActions}
        {ai.available && (
          <span className="inline-flex items-center gap-1">
            <AiButton variant="link" onClick={() => setAiPlanOpen(true)}>
              Terv AI-val
            </AiButton>
            <HelpHint id="trainings.aiPlan" />
          </span>
        )}
      </div>
    </>
  );
};

export default PlanBlocksEditor;
